/**
 * Agent List Page
 * Displays list of all agent configurations with search and filtering
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentsListQuery } from '@/lib/queries';
import { Loading, ErrorMessage } from '@/components/common';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { CreateAgentDialog } from '@/features/agent-config/components';
import { useI18n } from '@/i18n';
import { Plus } from 'lucide-react';
import type { AgentListItem } from '@/types';

export const AgentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { messages, language } = useI18n();
  const t = messages.agentConfig;
  const tc = messages.common;
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data: agents, isLoading, error, refetch } = useAgentsListQuery(showInactive);

  // Filter agents based on search term
  const filteredAgents = useMemo(() => {
    if (!agents) return [];
    if (!searchTerm.trim()) return agents;

    const term = searchTerm.toLowerCase();
    return agents.filter(
      (agent) =>
        agent.name.toLowerCase().includes(term) ||
        agent.agent_type.toLowerCase().includes(term) ||
        agent.description.toLowerCase().includes(term)
    );
  }, [agents, searchTerm]);

  const handleAgentClick = (agentType: string) => {
    navigate(`/agents/${agentType}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US');
  };

  if (isLoading) {
    return <Loading message={tc.loading} />;
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage error={error.message} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t.listTitle}</h1>
          <p className="text-slate-600 mt-1">
            {t.listDescription}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          variant="primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t.createAgent}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            type="text"
            placeholder={t.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300"
          />
          {t.showInactive}
        </label>
        <Button variant="secondary" onClick={() => refetch()}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {tc.refresh}
        </Button>
      </div>

      {/* Agent List */}
      {filteredAgents.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-gray-500">
            {searchTerm ? (
              <>
                <p className="text-lg">{t.noAgentsMatch}</p>
                <p className="text-sm mt-1">{t.noAgentsMatchHint}</p>
              </>
            ) : (
              <>
                <p className="text-lg">{t.noAgentsYet}</p>
                <p className="text-sm mt-1">{t.noAgentsYetHint}</p>
              </>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredAgents.map((agent) => (
            <AgentCard
              key={agent.agent_type}
              agent={agent}
              onClick={() => handleAgentClick(agent.agent_type)}
              formatDate={formatDate}
              translations={t}
            />
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-slate-500 pt-4 border-t">
        <span>
          {t.showingAgents.replace('{current}', String(filteredAgents.length)).replace('{total}', String(agents?.length || 0))}
        </span>
        <span>
          {t.activeCount.replace('{count}', String(agents?.filter((a) => a.is_active).length || 0))}
        </span>
      </div>

      {/* Create Agent Dialog */}
      <CreateAgentDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={(agentType) => navigate(`/agents/${agentType}`)}
      />
    </div>
  );
};

interface AgentCardProps {
  agent: AgentListItem;
  onClick: () => void;
  formatDate: (date: string) => string;
  translations: typeof import('@/i18n/messages').default['zh']['agentConfig'];
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, onClick, formatDate, translations: t }) => {
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow ${
        !agent.is_active ? 'opacity-60' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">{agent.name}</h3>
            <span className="text-sm text-gray-500 font-mono">({agent.agent_type})</span>
            {!agent.is_active && (
              <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                {t.inactive}
              </span>
            )}
          </div>
          <p className="text-gray-600 mt-1 line-clamp-2">{agent.description}</p>
        </div>

        <div className="text-right text-sm text-gray-500 ml-4">
          <div>{t.version} {agent.version}</div>
          <div className="text-xs mt-1">
            {t.updated}: {formatDate(agent.updated_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentListPage;
